import type {
  ActionEnvelope,
  ChatAction,
  ChatState,
  StateAction,
  ToolCallRunningState,
  ToolCallStreamingState,
} from '@microsoft/agent-host-protocol'
import {chatReducer} from '@microsoft/agent-host-protocol'
import type {ObservableRelaySink} from './relay-registry'

const ACTION_TOOL_CALL_START = 'chat/toolCallStart'
const ACTION_TOOL_CALL_DELTA = 'chat/toolCallDelta'
const ACTION_TOOL_CALL_CONTENT_CHANGED = 'chat/toolCallContentChanged'
const ACTION_TOOL_CALL_READY = 'chat/toolCallReady'
const ACTION_TOOL_CALL_CONFIRMED = 'chat/toolCallConfirmed'
const ACTION_TOOL_CALL_COMPLETE = 'chat/toolCallComplete'
const ACTION_TOOL_CALL_RESULT_CONFIRMED = 'chat/toolCallResultConfirmed'
const ACTION_SESSION_TITLE_CHANGED = 'session/titleChanged'
const PART_TOOL_CALL = 'toolCall'
const CONTENT_TYPE_TEXT = 'text'
const TOOL_STATUS_STREAMING = 'streaming'
const TOOL_STATUS_RUNNING = 'running'
const TOOL_STATUS_PENDING_CONFIRMATION = 'pending-confirmation'
const TOOL_STATUS_PENDING_RESULT_CONFIRMATION = 'pending-result-confirmation'

// Incremental tool *output* rides on a `chat/toolCallDelta`'s per-action `_meta`
// rather than its `content`, which is reserved for partial tool *arguments*.
// Two mutually-exclusive conventions carry it:
//
//  * `ptyTerminal` — AHP's portable terminal convention, shaped
//    `{input: string, output: string}`. Used by PTY-backed tools (`bash`,
//    `powershell`, `local_shell`); `input` is empty on output chunks because the
//    command rode the tool-call arguments.
//  * `copilot.toolOutputDelta` — a plain string. The producer's fallback for
//    every other tool, and for any call whose kind it never recorded.
//
// Chunks are cumulative fragments, not snapshots, so they are appended in
// arrival order. The producer never stamps an empty value, so an empty chunk
// means "no output" and is ignored.
const META_KEY_PTY_TERMINAL = 'ptyTerminal'
const META_KEY_TOOL_OUTPUT_DELTA = 'copilot.toolOutputDelta'

/**
 * `_meta` key carrying the *accumulated* tool output for a `streaming` call.
 *
 * This is synthesized here, not a wire key: the producer's `ptyTerminal` /
 * `copilot.toolOutputDelta` keys each carry a single chunk, and the reducer
 * replaces `_meta` wholesale on every delta, so the running total has nowhere
 * else to live. `ToolCallStreamingState` has no `content` field and
 * `chat/toolCallContentChanged` is rejected for anything but `running`, which
 * leaves `_meta` as the only state a streaming call can carry output on.
 *
 * Read by `ahpToolCallToToolProps` in `@github-ui/agent-sessions`.
 */
export const STREAMED_TOOL_OUTPUT_META_KEY = 'github.streamedToolOutput'

// Terminal tool-call statuses: a buffered Ready can never surface a confirmation
// once its tool reaches one of these, so we stop tracking it.
const TERMINAL_TOOL_STATUSES: ReadonlySet<string> = new Set(['completed', 'cancelled'])

// Turn-terminal actions. AHP's reducer force-cancels every non-terminal tool
// call in the active turn on these, so a buffered Ready for that turn can never
// surface a valid confirmation again. We drop those readies to (a) bound
// retention — a Ready whose `chat/toolCallStart` never arrived leaves no tool
// part behind, so it would otherwise linger for the store's lifetime — and (b)
// stop a later reconnect snapshot, which may report the tool as `streaming`
// again, from re-opening the already-cancelled prompt.
const TURN_TERMINAL_ACTIONS: ReadonlySet<string> = new Set(['chat/turnComplete', 'chat/turnCancelled', 'chat/error'])

/**
 * Narrow a wire `StateAction` to the chat-scoped subset the reducer accepts.
 */
function isChatAction(action: StateAction): action is ChatAction {
  return action.type.startsWith('chat/')
}

function toolCallKey(action: StateAction): string | undefined {
  const toolCallId = (action as {toolCallId?: unknown}).toolCallId
  return typeof toolCallId === 'string' ? toolCallId : undefined
}

function turnKey(action: StateAction): string | undefined {
  const turnId = (action as {turnId?: unknown}).turnId
  return typeof turnId === 'string' ? turnId : undefined
}

interface ToolReplayState {
  readyAction: ChatAction
  confirmedAction?: ChatAction
  completeAction?: ChatAction
}

/**
 * The incremental tool output a `chat/toolCallDelta` carries on its `_meta`, or
 * `undefined` when it carries none (an arguments-only delta).
 */
function toolOutputChunk(action: StateAction): string | undefined {
  const meta = (action as {_meta?: Record<string, unknown>})._meta
  if (!meta) return undefined

  const pty = meta[META_KEY_PTY_TERMINAL]
  if (pty && typeof pty === 'object') {
    const output = (pty as {output?: unknown}).output
    if (typeof output === 'string' && output) return output
  }

  const fallback = meta[META_KEY_TOOL_OUTPUT_DELTA]
  return typeof fallback === 'string' && fallback ? fallback : undefined
}

/**
 * The `streaming` or `running` tool call with `toolCallId` in the active turn,
 * or `undefined`.
 *
 * Deliberately scans only the active turn: streamed output arrives per chunk, so
 * this runs on a hot path, and an in-flight tool always lives in the active turn.
 */
function inFlightToolCall(
  state: ChatState,
  toolCallId: string,
): ToolCallStreamingState | ToolCallRunningState | undefined {
  for (const part of state.activeTurn?.responseParts ?? []) {
    if (part.kind === PART_TOOL_CALL && part.toolCall.toolCallId === toolCallId) {
      const {status} = part.toolCall
      return status === TOOL_STATUS_STREAMING || status === TOOL_STATUS_RUNNING ? part.toolCall : undefined
    }
  }
  return undefined
}

/**
 * Current status of the tool call with `toolCallId`, or `undefined` when no such
 * tool part exists yet (its `chat/toolCallStart` has not been folded in).
 * `toolCallId` is session-unique by the copilot-host producer contract, so this
 * intentionally searches across turns without including `turnId` in the key.
 */
function toolCallStatus(state: ChatState, toolCallId: string): string | undefined {
  const turns = [...(state.turns ?? []), ...(state.activeTurn ? [state.activeTurn] : [])]
  for (const turn of turns) {
    for (const part of turn.responseParts) {
      if (part.kind === PART_TOOL_CALL && part.toolCall.toolCallId === toolCallId) {
        return part.toolCall.status
      }
    }
  }
  return undefined
}

/**
 * Maintains the AHP `ChatState` for one relay session and exposes it
 * as an external store for `useSyncExternalStore`.
 */
export class AhpChatStore implements ObservableRelaySink {
  #state: ChatState | undefined
  #seeded = false
  #disposed = false
  // Work around copilotd occasionally publishing permission prompts
  // before their matching tool start. The observed wire order is:
  // `chat/toolCallReady` (confirmed absent, carries confirmation UI data) then
  // `chat/toolCallStart` for the same toolCallId. AHP's official chatReducer
  // intentionally ignores Ready unless the tool already exists in streaming or
  // running state, so without this buffer the UI renders a bare streaming tool
  // (often as "undefined") and never shows confirmation choices.
  //
  // Upstream copilotd places these on separate async paths:
  // - SDK `tool.execution_start` -> SessionPipelineEvent::Cli -> ChatToolCallStart
  // - permission callback -> SessionPipelineEvent::Permission -> ChatToolCallReady
  // The host should serialize these so Start reaches clients before Ready; once
  // that is fixed, this compatibility buffer can be removed.
  //
  // The reorder became far more common under copilot-host protocol 0.5.1, which
  // routinely emits Ready before Start. A reconnect (which happens at least once
  // per session) re-seeds state via `applySnapshot`, and the host's snapshot
  // reports the tool as `streaming` again because its confirmation lives on a
  // separate path. We therefore RETAIN a buffered Ready across snapshots and
  // re-derive the confirmation whenever its tool is present and still
  // `streaming` (see `#drainPendingToolReady`).
  //
  // Retain the non-terminal transition chain (Ready, Confirmed, and a Complete
  // that requires result confirmation) so a reconnect snapshot that resets the
  // tool to `streaming` can reconstruct the latest prompt without stalling.
  // Retention is bounded by result confirmation, a terminal snapshot status, or
  // the turn ending.
  readonly #pendingToolReplay = new Map<string, ToolReplayState>()
  // Live tool output accumulated from `chat/toolCallDelta` `_meta`, keyed by
  // tool call id. The reducer ignores that `_meta`, so without this the body of
  // a running tool stays empty until its result lands. Entries are dropped when
  // the tool settles (its result supersedes them) or its turn ends.
  readonly #streamedToolOutput = new Map<string, string>()
  readonly #listeners = new Set<() => void>()

  /**
   * No-op: the store owns its own state, so there is nothing to take ownership
   * of before events arrive. Present to satisfy the {@link RelaySessionSink}
   * contract the relay registry drives.
   */
  start(): void {}

  /** Seed (or re-seed, on reconnect) the state wholesale from a snapshot. */
  applySnapshot(state: ChatState): void {
    if (this.#disposed) return
    this.#state = state
    this.#seeded = true
    // Re-derive buffered readies across reconnect snapshots (0.5.1 emits Ready
    // before Start, and a reconnect snapshot resets a locally-confirmed tool to
    // `streaming`): surface any whose tool is present, leave the rest buffered
    // for a future Start rather than dropping the confirmation.
    this.#drainPendingToolReady()
    // A snapshot replaces the state wholesale, discarding output we had folded
    // into still-running tools. Re-apply it so a reconnect mid-command does not
    // blank the terminal body.
    for (const toolCallId of this.#streamedToolOutput.keys()) {
      this.#applyStreamedToolOutput(toolCallId)
    }
    this.#emit()
  }

  /** Fold a single live action into the current state. */
  applyAction(env: ActionEnvelope): void {
    if (this.#disposed || !this.#seeded || !this.#state) return

    if (env.action.type === ACTION_SESSION_TITLE_CHANGED) {
      const nextTitle = (env.action as {title?: unknown}).title
      if (typeof nextTitle === 'string' && nextTitle !== this.#state.title) {
        this.#state = {...this.#state, title: nextTitle}
        this.#emit()
      }
      return
    }

    if (!isChatAction(env.action)) return

    if (env.action.type === ACTION_TOOL_CALL_READY) {
      const toolCallId = toolCallKey(env.action)
      if (toolCallId && toolCallStatus(this.#state, toolCallId) === undefined) {
        this.#pendingToolReplay.set(toolCallId, {readyAction: env.action})
        return
      }
    }

    this.#state = chatReducer(this.#state, env.action)

    if (env.action.type === ACTION_TOOL_CALL_READY) {
      const toolCallId = toolCallKey(env.action)
      const status = toolCallId ? toolCallStatus(this.#state, toolCallId) : undefined
      if (toolCallId && (status === TOOL_STATUS_PENDING_CONFIRMATION || status === TOOL_STATUS_RUNNING)) {
        this.#pendingToolReplay.set(toolCallId, {readyAction: env.action})
      }
    } else if (env.action.type === ACTION_TOOL_CALL_DELTA) {
      this.#accumulateToolOutput(env.action)
    } else if (env.action.type === ACTION_TOOL_CALL_START) {
      // `chat/toolCallStart` is the only action that introduces the `streaming`
      // tool a buffered Ready targets, so gate the (history-scanning) drain to
      // it — streamed `chat/delta`/`chat/toolCallDelta` tokens must not pay that
      // cost on every token.
      this.#drainPendingToolReady()
    } else if (env.action.type === ACTION_TOOL_CALL_CONFIRMED) {
      const toolCallId = toolCallKey(env.action)
      const replay = toolCallId ? this.#pendingToolReplay.get(toolCallId) : undefined
      if (replay) replay.confirmedAction = env.action
    } else if (env.action.type === ACTION_TOOL_CALL_COMPLETE) {
      const toolCallId = toolCallKey(env.action)
      // The result supersedes anything we streamed into the body.
      if (toolCallId) this.#streamedToolOutput.delete(toolCallId)
      const replay = toolCallId ? this.#pendingToolReplay.get(toolCallId) : undefined
      const status = toolCallId ? toolCallStatus(this.#state, toolCallId) : undefined
      if (replay && (status === undefined || status === TOOL_STATUS_PENDING_RESULT_CONFIRMATION)) {
        replay.completeAction = env.action
      } else if (toolCallId) {
        this.#pendingToolReplay.delete(toolCallId)
      }
    } else if (env.action.type === ACTION_TOOL_CALL_RESULT_CONFIRMED) {
      const toolCallId = toolCallKey(env.action)
      if (toolCallId) this.#pendingToolReplay.delete(toolCallId)
    } else if (TURN_TERMINAL_ACTIONS.has(env.action.type)) {
      // The turn ended: the reducer force-cancelled its tools, so forget every
      // buffered Ready for that turn. Bounds retention when Start never arrived
      // and prevents a reconnect snapshot from re-opening a cancelled prompt.
      const turnId = turnKey(env.action)
      if (turnId) this.#forgetTurnReadies(turnId)
      // The reducer force-cancelled the turn's tools, so nothing can still be
      // streaming. Clearing here bounds retention for tools that never completed.
      this.#streamedToolOutput.clear()
    }

    this.#emit()
  }

  /**
   * Append a `chat/toolCallDelta`'s streamed output chunk and fold the running
   * total into its tool call.
   */
  #accumulateToolOutput(action: ChatAction): void {
    const toolCallId = toolCallKey(action)
    const chunk = toolOutputChunk(action)
    if (!toolCallId || !chunk) return

    this.#streamedToolOutput.set(toolCallId, (this.#streamedToolOutput.get(toolCallId) ?? '') + chunk)
    this.#applyStreamedToolOutput(toolCallId)
  }

  /**
   * Publish the accumulated output for `toolCallId` so consumers render it
   * exactly as they render a settled result.
   *
   * The two in-flight states carry it differently. A `running` call gets it as
   * content, republished in full rather than appended: `_meta` output chunks are
   * not part of `ToolCallState`, so this is the only place they surface, and
   * `chat/toolCallContentChanged` replaces content wholesale. Blocks the
   * producer set (a terminal handle, a subagent reference) are preserved and the
   * streamed text is carried in a single trailing text block, which keeps
   * republishing idempotent.
   *
   * A `streaming` call has no `content` field and is rejected by
   * `chat/toolCallContentChanged`, so it carries the total on `_meta` instead.
   * This is the path server tools actually take: copilot-host defers the Ready
   * that would promote them to `running` until the call completes, so they
   * stream their whole execution in `streaming`.
   */
  #applyStreamedToolOutput(toolCallId: string): void {
    if (!this.#state) return
    const output = this.#streamedToolOutput.get(toolCallId)
    if (!output) return

    const toolCall = inFlightToolCall(this.#state, toolCallId)
    if (!toolCall) return

    const turnId = this.#state.activeTurn?.id ?? ''

    if (toolCall.status === TOOL_STATUS_STREAMING) {
      this.#state = chatReducer(this.#state, {
        type: ACTION_TOOL_CALL_DELTA,
        turnId,
        toolCallId,
        // No `content`: that would append to `partialInput`, corrupting the
        // tool's arguments with its output.
        _meta: {...toolCall._meta, [STREAMED_TOOL_OUTPUT_META_KEY]: output},
      } as ChatAction)
      return
    }

    const producerBlocks = (toolCall.content ?? []).filter(block => block.type !== CONTENT_TYPE_TEXT)
    this.#state = chatReducer(this.#state, {
      type: ACTION_TOOL_CALL_CONTENT_CHANGED,
      turnId,
      toolCallId,
      content: [...producerBlocks, {type: CONTENT_TYPE_TEXT, text: output}],
    } as ChatAction)
  }

  /**
   * Re-derive buffered out-of-order tool transitions after a snapshot or Start.
   * Historical turns and terminal tools retire their replay state. Streaming
   * tools replay Ready, Confirmed, and Complete when present, so reconnect restores
   * either the execution state or the pending result-confirmation prompt.
   */
  #drainPendingToolReady(): void {
    if (!this.#state || this.#pendingToolReplay.size === 0) return
    for (const [toolCallId, replay] of this.#pendingToolReplay) {
      const turnId = turnKey(replay.readyAction)
      if (turnId && this.#state.turns.some(turn => turn.id === turnId)) {
        this.#pendingToolReplay.delete(toolCallId)
        continue
      }

      const status = toolCallStatus(this.#state, toolCallId)
      if (status === undefined) continue // Start not folded in yet — keep buffered.
      if (status === TOOL_STATUS_STREAMING) {
        this.#state = chatReducer(this.#state, replay.readyAction)
        if (replay.confirmedAction) {
          this.#state = chatReducer(this.#state, replay.confirmedAction)
        }
        if (replay.completeAction) {
          this.#state = chatReducer(this.#state, replay.completeAction)
          const replayedStatus = toolCallStatus(this.#state, toolCallId)
          if (replayedStatus && TERMINAL_TOOL_STATUSES.has(replayedStatus)) {
            this.#pendingToolReplay.delete(toolCallId)
          }
        }
      } else if (TERMINAL_TOOL_STATUSES.has(status)) {
        // Confirmation can never apply to a finished tool; stop tracking it.
        this.#pendingToolReplay.delete(toolCallId)
      }
    }
  }

  /**
   * Forget every buffered `chat/toolCallReady` belonging to `turnId`. Called on
   * turn-terminal actions (`chat/turnComplete` / `turnCancelled` / `error`),
   * which the reducer treats by force-cancelling the turn's non-terminal tools.
   * A Ready for a finished turn can never surface a valid confirmation again, so
   * dropping it bounds retention (the Start may never have arrived, leaving no
   * tool part to terminate it via `#drainPendingToolReady`) and prevents a later
   * reconnect snapshot from re-opening the cancelled prompt.
   */
  #forgetTurnReadies(turnId: string): void {
    for (const [toolCallId, replay] of this.#pendingToolReplay) {
      if (turnKey(replay.readyAction) === turnId) this.#pendingToolReplay.delete(toolCallId)
    }
  }

  dispose(): void {
    this.#disposed = true
    this.#pendingToolReplay.clear()
    this.#listeners.clear()
  }

  /** Current `ChatState`, or `undefined` before the first snapshot. */
  getSnapshot(): ChatState | undefined {
    return this.#state
  }

  /**
   * Register a listener fired on every state change. Returns an unsubscribe function.
   */
  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener)
    return () => this.#listeners.delete(listener)
  }

  #emit(): void {
    for (const listener of this.#listeners) listener()
  }
}
